import { useState, useEffect } from 'react';
import TransferService from '../services/transferService';

/**
 * Hook to fetch the current user's remaining transfer budget for a given bid.
 *
 * @param {string} bidId Mongo ObjectId of the bid
 * @param {string|null} leagueId Explicit league id from the notification being handled
 * @returns {{ remainingBudget: number|null, teamId: string|null, loading: boolean, error: string|null }}
 */
export default function useSellerBudget(bidId, leagueId = null) {
  const resolvedLeagueId = leagueId || null;
  const [remainingBudget, setRemainingBudget] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bidId || !resolvedLeagueId) {
      setRemainingBudget(null);
      setTeamId(null);
      setError(null);
      return undefined;
    }

    let isCancelled = false;
    async function fetchBudget() {
      setLoading(true);
      setError(null);
      try {
        const teamRes = await TransferService.getTeamInfo(resolvedLeagueId);
        const teamData = teamRes.data || {};
        const budget = teamData.remainingBudget ?? teamData.budget ?? null;
        if (!isCancelled) {
          setRemainingBudget(budget);
          setTeamId(teamData.teamId ?? null);
        }
      } catch (err) {
        if (!isCancelled) {
          setTeamId(null);
          setError(err.response?.data?.error || err.message || 'Failed to fetch budget');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    fetchBudget();
    return () => { isCancelled = true; };
  }, [bidId, resolvedLeagueId]);

  return { remainingBudget, teamId, loading, error };
}
